import type { JSONContent } from "@tiptap/core";
import type { DateRangePresetKey } from "../../util/dateRangePresets";
import Fetcher from "../Fetcher.ts";
import { getHeaders, PURCHASER_API_BASE_URL } from "../Constants.ts";
import type { FileUpload } from "./FileUpload.ts";
import type { RFQCommunication } from "./RFQMailbox.ts";
import type { Mailbox } from "./Mailbox.ts";
import type { RFQStatus, TabularColumnDefinition, TabularRow } from "./RFQ.ts";

// The PO line-item table, built directly from the PO document's extracted facts.
export type PurchaseOrderLineItems = {
    columnDefinitions: TabularColumnDefinition[];
    rows: TabularRow[];
};

// factIds ties a value back to the document_memory fact(s) it came from
export type CitedValue = { value: string; factIds: number[] };

// A generic Overview section. `rows` are whatever key/values the section's extraction carried — not a fixed
// field set — and `notes` are prose lines. `kind` drives its icon and where it renders: "vendor"/"shipping"
// are cards (the vendor card is titled by its company); "notes" is the PO Notes block below the table. Every
// row and note carries factIds so its value can cite back to the document.
export type PurchaseOrderHeaderSection = {
    title: string;
    kind: "vendor" | "shipping" | "notes";
    rows: { label: string; value: string; factIds: number[] }[];
    notes: { text: string; factIds: number[] }[];
};

// Curated known fields, each carrying factIds. vendorName titles the vendor card; the rest fill the left panel.
export type PurchaseOrderSummary = {
    vendorName: CitedValue | null;
    // The vendor's own order number, which only their own documents carry.
    orderNumber: CitedValue | null;
    total: { amount: number; currency: string | null; factIds: number[] } | null;
    poCreatedDate: CitedValue | null;
    paymentTerms: CitedValue | null;
};

export type PurchaseOrderConfirmationStatus = "CONFIRMED" | "NOT_CONFIRMED" | "PENDING";

/** Where a purchase order came from: an RFQ, sent on its own, or imported. */
export type PurchaseOrderSource = "RFQ" | "STANDALONE" | "IMPORTED";

// A PO's confirmation state, resolved by the API.
export type PurchaseOrderConfirmation = {
    status: PurchaseOrderConfirmationStatus;
    confirmedAt: string | null;
    // Who confirmed it: the vendor's address, or the person who marked it.
    confirmedByEmail: string | null;
    // The vendor reply that confirmed the PO, for the card's message row.
    message: { communicationId: number; fromEmail: string | null; message: string } | null;
};

// Purchase orders hang off a mailbox (a vendor within an RFQ) rather than an
// award, so a PO can be sent to any vendor at any time regardless of whether an
// award exists.
export type RFQPurchaseOrder = {
    id: number;
    poNumber: string | null;
    createdAt: string;
    updatedAt: string;
    teamId: number;
    fileId: number | null;
    file?: FileUpload;
    confirmationFileId: number | null;
    confirmationFile?: FileUpload;
    communicationId: number | null;
    communication?: RFQCommunication;
    acknowledgedAt: string | null;
    acknowledgedByCommunicationId: number | null;
};

// Flat display shape for the team-wide Purchase Orders index. Mirrors the
// backend `PurchaseOrderListEntry` (routes/purchase-orders.ts). PO total and
// line items are intentionally omitted for now.
export type PurchaseOrderListEntry = {
    id: number;
    poNumber: string | null;
    // The PO document's file name, or the confirmation's when there is no PO document.
    fileName: string | null;
    // When the PO was sent, or when it was imported.
    sentAt: string;
    sentByName: string | null;
    // The vendor it was sent to, or the name read off the document.
    vendorName: string | null;
    // Vendor relationship id, for linking to the vendor's page. An extracted name has none.
    vendorId: number | null;
    // The mailbox holding this PO's email thread. An imported PO has none.
    mailboxId: number | null;
    // The originating RFQ, or null for a standalone (direct-to-vendor) PO.
    rfqUniquePublicId: string | null;
    // Derived status of the originating RFQ (powers RFQStatusMeta); null when standalone.
    rfqStatus: RFQStatus | null;
    // When the vendor acknowledged the PO, or null if not yet acknowledged.
    acknowledgedAt: string | null;
    confirmationStatus: PurchaseOrderConfirmationStatus;
    // Un-confirming would drop an uploaded confirmation document.
    hasConfirmationDocument: boolean;
    // The order total read off the document.
    total: { amount: number; currency: string | null } | null;
};

/** The delivery statuses the API returns. */
export const DELIVERY_STATUSES = ["Delivered", "OnSchedule", "BehindSchedule", "PastDue"] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

/**
 * The delivery state resolved from the change log.
 */
export type PurchaseOrderLineItemDeliveryState = {
    initialDeliveryDate: string | null;
    currentDeliveryDate: string | null;
    deliveryStatus: DeliveryStatus | null;
};

export type PurchaseOrderLineItemRow = PurchaseOrderLineItemDeliveryState & {
    rowId: number;
    purchaseOrderId: number;
    poNumber: string | null;
    poSentAt: string;
    vendorName: string | null;
    // Keyed by the template's column names, so every PO's row has the same shape.
    values: Record<string, string | number | null>;
};

export type PurchaseOrderLineItemListResponse = {
    results: PurchaseOrderLineItemRow[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
};

export type PurchaseOrderLineItemField = "DeliveryDate" | "DeliveryStatus";

/** One field on one line touched by an action. A single edit has one; a bulk update or reading the document has many. */
export type PurchaseOrderLineItemChange = {
    rowId: number;
    lineLabel: string | null;
    field: PurchaseOrderLineItemField;
    previousValue: string | null;
    newValue: string;
};

/** One action, however many lines and fields it touched: a buyer's save, or the dates read from the document. */
export type PurchaseOrderLineItemChangeset = {
    id: string;
    changedAt: string;
    /** "system" is the platform stating where a value came from; "user" is a buyer's edit. */
    source: "user" | "system";
    /** Null for system changesets, which have no author. */
    changedByName: string | null;
    note: string | null;
    changes: PurchaseOrderLineItemChange[];
};

/** A field left out is left alone on every row. At least one of the two must be set. */
export type PurchaseOrderLineItemChangesetParams = {
    rowIds: number[];
    deliveryDate?: string;
    deliveryStatus?: DeliveryStatus;
    note?: string;
};

export type PurchaseOrderListParams = {
    search?: string;
    sort?: string;
    sortDirection?: "asc" | "desc";
    page?: number;
    limit?: number;
    statuses?: PurchaseOrderConfirmationStatus[];
    vendorIds?: number[];
    sentByIds?: number[];
    sentStart?: number;
    sentEnd?: number;
    source?: PurchaseOrderSource;
    /** False skips the filter counts, for a page past the one the menus were built from. */
    facets?: boolean;
    /** The sent date ranges to count for the date filter, worked out in the viewer's timezone. */
    dateSentCounts?: { key: DateRangePresetKey; start?: number; end?: number }[];
};

/**
 * What the filter pickers offer, each with how many purchase orders would be left if you picked
 * it. A facet's count reflects the other applied filters but not its own.
 */
export type PurchaseOrderListFacets = {
    statuses: { value: PurchaseOrderConfirmationStatus; count: number }[];
    sources: { value: PurchaseOrderSource; count: number }[];
    vendors: { id: number; name: string; count: number }[];
    senders: { id: number; name: string; count: number }[];
    /** Counts for the ranges asked for in dateSentCounts, by key. */
    dateSent: Partial<Record<DateRangePresetKey, number>>;
};

export type PurchaseOrderListResponse = {
    results: PurchaseOrderListEntry[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
    // Only the first page carries these; the menus do not change as more pages load.
    facets?: PurchaseOrderListFacets;
};

// Detail shape for the PO entity page. Adds the file record + a presigned URL so
// the page can load the document in the FileViewer.
export type PurchaseOrderDetail = {
    id: number;
    poNumber: string | null;
    fileName: string | null;
    file: FileUpload | null;
    signedUrl: string | null;
    confirmationFile: FileUpload | null;
    confirmationSignedUrl: string | null;
    sentAt: string;
    sentByName: string | null;
    vendorName: string | null;
    vendorId: number | null;
    // The mailbox holding this PO's email thread. An imported PO has none.
    mailboxId: number | null;
    rfqUniquePublicId: string | null;
    rfqStatus: RFQStatus | null;
    // When the vendor acknowledged the PO, or null if not yet acknowledged.
    acknowledgedAt: string | null;
    confirmation: PurchaseOrderConfirmation;
    extractionError: string | null;
    lineItems: PurchaseOrderLineItems | null;
    // The PO line items leveling view output. Same columns on every PO, unlike lineItems above,
    // whose columns are whatever the document called them. Null until the extraction has run.
    levelingLineItems: {
        outputId: string;
        title: string;
        columns: string[];
        rows: (PurchaseOrderLineItemDeliveryState & {
            // The extracted row id, which edits are keyed off of.
            rowId: number;
            values: (string | number | null)[];
            deliveryStatusIsManual: boolean;
        })[];
    } | null;
    // The confirmation's own concepts, shown alongside the PO's.
    confirmationLineItems: PurchaseOrderLineItems | null;
    confirmationSummary: PurchaseOrderSummary | null;
    // The confirmation's extraction is still running.
    confirmationExtracting: boolean;
    header: PurchaseOrderHeaderSection[] | null;
    summary: PurchaseOrderSummary | null;
};

// The PO's email thread. Matches the /v2/purchase-orders/:id/mailbox response.
export type PurchaseOrderMailbox = Mailbox & {
    // The message in the thread that has the PO attached.
    poCommunicationId: number | null;
};

export type SendPOParams = {
    mailboxId: number;
    fileId?: number;
    attachmentFileIds?: number[];
    content?: string;
    contentJson: JSONContent;
    poNumber?: string;
    senderId: number;
    recipientEmails?: string[];
};

// Create a standalone PO: sent directly to a vendor with its own mailbox and no RFQ.
// The backend creates the mailbox, sends the first email, and records the PO.
export type CreateStandalonePOParams = {
    vendorRelationshipId: number;
    fileId: number;
    attachmentFileIds?: number[];
    content?: string;
    contentJson: JSONContent;
    poNumber?: string;
    subject?: string;
    senderId: number;
    recipientEmails: string[];
};

// Import a PO placed outside Purchaser. Nothing is sent, so it has no email thread.
export type ImportPOParams = {
    fileId?: number;
    confirmationFileId?: number;
    poNumber?: string;
};

const f = Fetcher.default();

export default {
    getRFQPurchaseOrders: (rfqPublicId: string) => {
        return f.get<RFQPurchaseOrder[]>(`/v2/rfqs/${rfqPublicId}/purchase-orders`);
    },

    // Team-wide list of all purchase orders (RFQ-originated and standalone).
    getPurchaseOrders: (params?: PurchaseOrderListParams) => {
        const qs = new URLSearchParams();
        if (params?.search) qs.set("search", params.search);
        if (params?.sort) qs.set("sort", params.sort);
        if (params?.page) qs.set("page", String(params.page));
        if (params?.limit) qs.set("limit", String(params.limit));
        if (params?.statuses !== undefined && params.statuses.length > 0) qs.set("statuses", params.statuses.join(","));
        if (params?.vendorIds !== undefined && params.vendorIds.length > 0) qs.set("vendorIds", params.vendorIds.join(","));
        if (params?.sentByIds !== undefined && params.sentByIds.length > 0) qs.set("sentByIds", params.sentByIds.join(","));
        if (params?.sentStart !== undefined) qs.set("sentStart", String(params.sentStart));
        if (params?.sentEnd !== undefined) qs.set("sentEnd", String(params.sentEnd));
        if (params?.source !== undefined) qs.set("source", params.source);
        if (params?.sortDirection !== undefined) qs.set("sortDirection", params.sortDirection);
        if (params?.facets === false) qs.set("facets", "false");
        if (params?.dateSentCounts !== undefined && params.dateSentCounts.length > 0) qs.set("dateSentCounts", JSON.stringify(params.dateSentCounts));
        const query = qs.toString();
        return f.get<PurchaseOrderListResponse>(`/v2/purchase-orders${query ? `?${query}` : ""}`);
    },

    // How many rows exportPurchaseOrders would return for the same window.
    getPurchaseOrderExportCount: (params: { startDate: number | null; endDate: number | null }) => {
        const qs = new URLSearchParams();
        if (params.startDate !== null) {
            qs.set("startDate", String(params.startDate));
        }
        if (params.endDate !== null) {
            qs.set("endDate", String(params.endDate));
        }
        return f.get<{ count: number }>(`/v2/purchase-orders/export/count?${qs.toString()}`);
    },

    // Returns a spreadsheet.
    exportPurchaseOrders: async (params: { startDate: number | null; endDate: number | null; columns: string[] }): Promise<Blob> => {
        const qs = new URLSearchParams();
        if (params.startDate !== null) {
            qs.set("startDate", String(params.startDate));
        }
        if (params.endDate !== null) {
            qs.set("endDate", String(params.endDate));
        }
        qs.set("columns", params.columns.join(","));

        const response = await fetch(`${PURCHASER_API_BASE_URL}/v2/purchase-orders/export?${qs.toString()}`, {
            headers: getHeaders(),
            method: "GET",
        });
        if (!response.ok) {
            throw new Error(`Export failed (${response.status})`);
        }
        return response.blob();
    },

    // How many line items the export would contain, plus the columns available to pick. The columns
    // come from the team's line items template, so they can't be hardcoded in the dialog.
    getPurchaseOrderLineItemExportCount: (params: {
        startDate: number | null;
        endDate: number | null;
        purchaseOrderId?: number;
        rowIds?: number[];
    }) => {
        const qs = new URLSearchParams();
        if (params.startDate !== null) {
            qs.set("startDate", String(params.startDate));
        }
        if (params.endDate !== null) {
            qs.set("endDate", String(params.endDate));
        }
        if (params.purchaseOrderId !== undefined) {
            qs.set("purchaseOrderId", String(params.purchaseOrderId));
        }
        if (params.rowIds !== undefined && params.rowIds.length > 0) {
            qs.set("rowIds", params.rowIds.join(","));
        }
        return f.get<{ count: number; columns: { key: string; header: string }[] }>(
            `/v2/purchase-orders/line-items/export/count?${qs.toString()}`
        );
    },

    // Returns a spreadsheet, one row per line item.
    exportPurchaseOrderLineItems: async (params: {
        startDate: number | null;
        endDate: number | null;
        columns: string[];
        /** Narrows the export to one purchase order, for the dialog on its own page. */
        purchaseOrderId?: number;
        /** Narrows it further to the rows a buyer ticked. */
        rowIds?: number[];
    }): Promise<Blob> => {
        const qs = new URLSearchParams();
        if (params.startDate !== null) {
            qs.set("startDate", String(params.startDate));
        }
        if (params.endDate !== null) {
            qs.set("endDate", String(params.endDate));
        }
        qs.set("columns", params.columns.join(","));
        if (params.purchaseOrderId !== undefined) {
            qs.set("purchaseOrderId", String(params.purchaseOrderId));
        }
        if (params.rowIds !== undefined && params.rowIds.length > 0) {
            qs.set("rowIds", params.rowIds.join(","));
        }

        const response = await fetch(`${PURCHASER_API_BASE_URL}/v2/purchase-orders/line-items/export?${qs.toString()}`, {
            headers: getHeaders(),
            method: "GET",
        });
        if (!response.ok) {
            throw new Error(`Export failed (${response.status})`);
        }
        return response.blob();
    },

    // Every extracted line item across the team's POs, for the PO Items view.
    getPurchaseOrderLineItems: (params: { page?: number; limit?: number; search?: string }) => {
        const qs = new URLSearchParams();
        if (params.page !== undefined) {
            qs.set("page", String(params.page));
        }
        if (params.limit !== undefined) {
            qs.set("limit", String(params.limit));
        }
        if (params.search !== undefined && params.search !== "") {
            qs.set("search", params.search);
        }
        const query = qs.toString();
        return f.get<PurchaseOrderLineItemListResponse>(`/v2/purchase-orders/line-items${query ? `?${query}` : ""}`);
    },

    // Append-only: records one buyer changeset across one or more line items and returns the refreshed table.
    recordPurchaseOrderLineItemChangeset: (purchaseOrderId: number, body: PurchaseOrderLineItemChangesetParams) => {
        return f.post<{ levelingLineItems: PurchaseOrderDetail["levelingLineItems"] }>(
            `/v2/purchase-orders/${purchaseOrderId}/line-item-changesets`,
            { body }
        );
    },

    getPurchaseOrderLineItemChangesets: (purchaseOrderId: number) => {
        return f.get<{ poNumber: string | null; poLabel: string; changesets: PurchaseOrderLineItemChangeset[] }>(
            `/v2/purchase-orders/${purchaseOrderId}/line-item-changesets`
        );
    },

    getPurchaseOrderMailbox: (purchaseOrderId: number) => {
        return f.get<PurchaseOrderMailbox | null>(`/v2/purchase-orders/${purchaseOrderId}/mailbox`);
    },

    getPurchaseOrder: (purchaseOrderId: number) => {
        return f.get<PurchaseOrderDetail | null>(`/v2/purchase-orders/${purchaseOrderId}`);
    },

    // Update editable PO fields
    updatePurchaseOrder: (purchaseOrderId: number, body: { poNumber: string }) => {
        return f.patch<{ id: number; poNumber: string | null }>(`/v2/purchase-orders/${purchaseOrderId}`, { body });
    },

    // Soft delete, allowed only for a purchase order with no mailbox.
    deletePurchaseOrder: (purchaseOrderId: number) => {
        return f.delete<void>(`/v2/purchase-orders/${purchaseOrderId}`, { body: {} });
    },

    sendPO: (params: SendPOParams) => {
        const { mailboxId, ...body } = params;
        return f.post<RFQPurchaseOrder | null>(`/mailboxes/${mailboxId}/purchase-orders`, { body });
    },

    createStandalonePO: (params: CreateStandalonePOParams) => {
        return f.post<RFQPurchaseOrder | null>(`/v2/purchase-orders`, { body: params });
    },

    importPO: (params: ImportPOParams) => {
        return f.post<RFQPurchaseOrder | null>(`/v2/purchase-orders/import`, { body: params });
    },

    // Attach the PO document to a purchase order imported without one.
    attachPurchaseOrderDocument: (purchaseOrderId: number, fileId: number) => {
        return f.post<RFQPurchaseOrder | null>(`/v2/purchase-orders/${purchaseOrderId}/document`, { body: { fileId } });
    },

    // Attach the vendor's confirmation document, which marks the PO confirmed.
    attachPurchaseOrderConfirmation: (purchaseOrderId: number, fileId: number) => {
        return f.post<RFQPurchaseOrder | null>(`/v2/purchase-orders/${purchaseOrderId}/confirmation`, { body: { fileId } });
    },

    // Confirm a PO by hand, or take that back. Un-confirming drops any uploaded confirmation document.
    setPurchaseOrderConfirmed: (purchaseOrderId: number, confirmed: boolean, removeConfirmationDocument?: boolean) => {
        return f.patch<{ id: number; confirmed: boolean; removedConfirmationDocument: boolean }>(
            `/v2/purchase-orders/${purchaseOrderId}/confirmation`,
            { body: { confirmed, removeConfirmationDocument } },
        );
    },
};
